import type { FC, HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'navy' | 'outline'

const TONES: Record<BadgeTone, string> = {
    neutral: 'bg-secondary text-foreground/85',
    primary: 'bg-primary-soft text-primary-deep',
    success: 'bg-success-soft text-success-strong',
    warning: 'bg-warning-soft text-warning-strong',
    danger: 'bg-destructive-soft text-destructive',
    navy: 'bg-navy text-white',
    outline: 'border border-border bg-card text-muted-foreground'
}

const DOTS: Record<BadgeTone, string> = {
    neutral: 'bg-subtle',
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-destructive',
    navy: 'bg-white',
    outline: 'bg-subtle'
}

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
    tone?: BadgeTone
    size?: 'sm' | 'md'
    dot?: boolean
    children: ReactNode
}

export const Badge: FC<BadgeProps> = ({
    tone = 'neutral',
    size = 'sm',
    dot = false,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm font-medium [&_svg]:size-3.5 [&_svg]:shrink-0',
                size === 'sm' ? 'h-6 px-2 text-caption' : 'h-7 px-2.5 text-small',
                TONES[tone],
                className
            )}
            {...props}
        >
            {dot && <span className={cn('size-1.5 rounded-full', DOTS[tone])} aria-hidden='true' />}
            {children}
        </span>
    )
}
